import React from 'react';
import { ChevronDown, History } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import {
  formatMasterDataChangeDate,
  formatMasterDataChangeTime,
  masterDataChangeKindLabel,
  type MasterDataChangeKind,
} from '../../utils/employeeMasterDataChanges';

type HistoryItem = {
  id: string;
  change_kind: MasterDataChangeKind;
  field_key: string;
  field_label: string;
  old_display: string;
  new_display: string;
  changed_at: string;
  changed_by_name: string;
};

type Props = {
  employeeId: string;
  /** Erhöhen, damit der Verlauf nach einem Speichern neu lädt. */
  refreshKey: number;
};

// „Änderung“ ist der Normalfall und bekommt keine Marke; nur Neuanlage und Löschung werden hervorgehoben.
const KIND_BADGE_CLASS: Record<Exclude<MasterDataChangeKind, 'update'>, string> = {
  create: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  delete: 'bg-red-50 text-red-700 border-red-200',
};

/** Einklappbarer Änderungsverlauf der lohnrelevanten Stammdaten eines Mitarbeiters. */
export function EmployeeMasterDataHistory({ employeeId, refreshKey }: Props) {
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<HistoryItem[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Lädt beim Aufklappen und nach jedem Speichern (refreshKey); nur open/employee/refreshKey als Abhängigkeiten,
  // sonst würde das Setzen von loading den laufenden Request über die Cleanup-Funktion abbrechen.
  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(`/api/employees/${encodeURIComponent(employeeId)}/master-data-changes`, {
          credentials: 'include',
        });
        const json = (await res.json()) as { items?: HistoryItem[]; error?: string };
        if (!res.ok) throw new Error(json.error || 'Änderungsverlauf konnte nicht geladen werden.');
        if (!cancelled) setItems(Array.isArray(json.items) ? json.items : []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Änderungsverlauf konnte nicht geladen werden.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, employeeId, refreshKey]);

  return (
    <section className="bg-app-surface border border-app-border rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.07)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-5 py-3 text-left"
      >
        <History className="w-4 h-4 text-app-text-muted" />
        <span className="text-sm font-semibold text-app-text">Änderungsverlauf</span>
        <span className="text-xs text-app-text-subtle">Lohnrelevante Stammdaten, alt → neu</span>
        <span className="flex-1" />
        <ChevronDown className={`w-4 h-4 text-app-text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-app-border px-5 py-3">
          {loading && (
            <div className="flex items-center gap-2 py-2 text-sm text-app-text-muted">
              <LoadingSpinner size="sm" /> Lädt…
            </div>
          )}
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
          )}
          {!loading && !error && items && items.length === 0 && (
            <p className="py-2 text-sm text-app-text-muted">Noch keine Änderungen protokolliert.</p>
          )}
          {!loading && !error && items && items.length > 0 && (
            <ul className="divide-y divide-app-border">
              {items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2 text-sm">
                  <span className="w-[118px] shrink-0 text-xs text-app-text-subtle tabular-nums">
                    {formatMasterDataChangeDate(item.changed_at)} {formatMasterDataChangeTime(item.changed_at)}
                  </span>
                  {item.change_kind !== 'update' && (
                    <span
                      className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0.5 text-[11px] font-medium ${KIND_BADGE_CLASS[item.change_kind]}`}
                    >
                      {masterDataChangeKindLabel(item.change_kind)}
                    </span>
                  )}
                  <span className="font-medium text-app-text">{item.field_label}</span>
                  {item.change_kind !== 'delete' && (
                    <span className="text-app-text-muted break-all">
                      <span className={item.old_display ? '' : 'italic text-app-text-subtle'}>
                        {item.old_display || 'leer'}
                      </span>
                      {' → '}
                      <span className={item.new_display ? 'text-app-text' : 'italic text-app-text-subtle'}>
                        {item.new_display || 'leer'}
                      </span>
                    </span>
                  )}
                  <span className="ml-auto shrink-0 text-xs text-app-text-subtle">{item.changed_by_name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
